import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useCare } from '../../contexts/CareContext'
import { useToast, humanizeError } from '../shared/Toast'

const CARE_STAGES = [
  { value: 'early', label: 'Early' },
  { value: 'middle', label: 'Middle' },
  { value: 'late', label: 'Late' },
]

const COMMON_CONDITIONS = [
  'Dementia', "Alzheimer's", "Parkinson's", 'Diabetes', 'Post-surgical',
  'Stroke', 'Cancer', 'Kidney disease', 'Heart condition', 'Fall risk',
  'Wandering', 'UTI prone', 'Arthritis',
]

/**
 * "Care for someone else" — a second entry point into creating a care
 * recipient, from inside an existing profile.
 *
 * Ginie onboarding is left completely alone: that branch just navigates to
 * /onboarding, which already handles creating another recipient for a user who
 * has one. The manual branch writes the care_recipients row directly — the
 * trg_on_care_recipient_created trigger grants the creator an admin
 * care_team_members row, so no membership insert is needed here.
 */
export default function AddRecipientSheet({ onCancel }) {
  const { user } = useAuth()
  const { reload, setActiveId } = useCare()
  const toast = useToast()
  const navigate = useNavigate()

  const [mode, setMode] = useState(null) // null | 'manual'
  const [f, setF] = useState({
    full_name: '',
    preferred_name: '',
    date_of_birth: '',
    care_stage: 'unknown',
    conditions: [],
  })
  const [customCondition, setCustomCondition] = useState('')
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((c) => ({ ...c, [k]: v }))

  const toggleCondition = (c) =>
    set('conditions', f.conditions.includes(c)
      ? f.conditions.filter((x) => x !== c)
      : [...f.conditions, c])

  function addCustomCondition() {
    const v = customCondition.trim()
    if (v && !f.conditions.includes(v)) set('conditions', [...f.conditions, v])
    setCustomCondition('')
  }

  async function save() {
    if (!f.full_name.trim()) return toast.error('Please enter their name.')
    setBusy(true)
    try {
      const { data, error } = await supabase
        .from('care_recipients')
        .insert({
          created_by: user.id,
          full_name: f.full_name.trim(),
          preferred_name: f.preferred_name.trim() || null,
          date_of_birth: f.date_of_birth || null,
          care_stage: f.care_stage,
          conditions: f.conditions,
        })
        .select()
        .single()
      if (error) throw error

      // Pull the new membership into context, then make it the active one so
      // the dashboard and the header switcher land on the person just added.
      await reload()
      setActiveId(data.id)
      toast.success(`${data.preferred_name || data.full_name} added.`)
      navigate('/')
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !busy && onCancel()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>
            {mode === 'manual' ? 'Add manually' : 'Care for someone else'}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>

        {mode === null ? (
          <div className="stack" style={{ gap: 10 }}>
            <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.55 }}>
              You can look after more than one person. Switch between them any
              time using the name at the top of the screen.
            </p>

            <button className="card card-tap" onClick={() => navigate('/onboarding')} style={{ textAlign: 'left', width: '100%' }}>
              <div className="row" style={{ gap: 10 }}>
                <span style={{ fontSize: 22 }} aria-hidden>✨</span>
                <div className="grow">
                  <div style={{ fontWeight: 700 }}>Start fresh with Ginie AI</div>
                  <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    Answer a few questions and get a care plan built for you.
                  </div>
                </div>
                <span className="muted" aria-hidden>→</span>
              </div>
            </button>

            <button className="card card-tap" onClick={() => setMode('manual')} style={{ textAlign: 'left', width: '100%' }}>
              <div className="row" style={{ gap: 10 }}>
                <span style={{ fontSize: 22 }} aria-hidden>✍️</span>
                <div className="grow">
                  <div style={{ fontWeight: 700 }}>Set up manually</div>
                  <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    Just the basics — you can fill in the rest later.
                  </div>
                </div>
                <span className="muted" aria-hidden>→</span>
              </div>
            </button>
          </div>
        ) : (
          <div className="stack">
            <div className="field">
              <label className="label" htmlFor="ar-name">Full name</label>
              <input id="ar-name" className="input" value={f.full_name} onChange={(e) => set('full_name', e.target.value)} placeholder="e.g. Margaret Singh" />
            </div>
            <div className="field">
              <label className="label" htmlFor="ar-pref">
                Preferred name <span className="hint">— optional</span>
              </label>
              <input id="ar-pref" className="input" value={f.preferred_name} onChange={(e) => set('preferred_name', e.target.value)} placeholder="What you call them — e.g. Ma" />
            </div>
            <div className="field">
              <label className="label" htmlFor="ar-dob">
                Date of birth <span className="hint">— optional</span>
              </label>
              <input id="ar-dob" type="date" className="input" value={f.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} />
            </div>

            <div className="field">
              <span className="label">Care stage</span>
              <div className="row wrap" style={{ gap: 8 }}>
                {CARE_STAGES.map((s) => (
                  <button
                    key={s.value} type="button"
                    className={`chip${f.care_stage === s.value ? ' chip-on' : ''}`}
                    aria-pressed={f.care_stage === s.value}
                    onClick={() => set('care_stage', f.care_stage === s.value ? 'unknown' : s.value)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <span className="label">Conditions</span>
              <div className="row wrap" style={{ gap: 8 }}>
                {COMMON_CONDITIONS.map((c) => (
                  <button
                    key={c} type="button"
                    className={`chip${f.conditions.includes(c) ? ' chip-on' : ''}`}
                    aria-pressed={f.conditions.includes(c)}
                    onClick={() => toggleCondition(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="row" style={{ gap: 8, marginTop: 8 }}>
                <input
                  className="input grow" value={customCondition}
                  onChange={(e) => setCustomCondition(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomCondition() } }}
                  placeholder="Something else"
                  aria-label="Add another condition"
                />
                <button type="button" className="btn btn-secondary" onClick={addCustomCondition}>Add</button>
              </div>
              {f.conditions.filter((c) => !COMMON_CONDITIONS.includes(c)).length > 0 && (
                <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
                  {f.conditions.filter((c) => !COMMON_CONDITIONS.includes(c)).map((c) => (
                    <span key={c} className="badge">
                      {c}
                      <button
                        type="button" onClick={() => toggleCondition(c)} aria-label={`Remove ${c}`}
                        style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, marginLeft: 2 }}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="row" style={{ gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setMode(null)} disabled={busy}>Back</button>
              <button className="btn btn-lg grow" onClick={save} disabled={busy}>
                {busy ? <span className="spinner" /> : 'Add them'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
